import { Address } from '../../types/Profile';
import { PostcardTemplate } from '../../types/misc';
import httpService from '../httpService';
import { DiscountResponse } from '../../models/orderService.model';
import { getImagePath } from '../../utils/imagePathUtils';
import { getSelectedCommunity } from '../../hooks/community/useSelectedCommunity';
import { PostcardOrder } from '../../types/communication';
import { store } from '../../redux/store';
import { RootState } from '../../redux/reducers';
import { updatePostcard } from '../../redux/slices/communicationsSlice';

export interface UploadCustomPostcardResponse {
  id: string;
  fileName: string;
  size: number;
  fileExtension: string;
}

export interface UploadCustomPostcardImageResponse {
  id: number;
  fileName: string;
  thumbnail: string;
  externalUploadedId: string;
  communityId: number;
}

const orderService = {
  placePostcardOrder: async (order: Omit<PostcardOrder, 'orderId'>): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    await httpService.post(`/networks/dashboards/${selectedDashboard}/orders/postcards`, order);
  },

  createPaymentIntent: async (amount: number): Promise<string> => {
    const res = await httpService.post('/payments/intent', { amount });
    return res.data.clientSecret;
  },

  getPostcardTemplates: async (): Promise<PostcardTemplate[]> => {
    const { data } = await httpService.get<PostcardTemplate[]>('/postcards/templates');
    return data.filter((t) => t.format === 'POSTCARD_SQ15');
  },

  getPostcardImagesByCommunityId: async (): Promise<UploadCustomPostcardImageResponse[]> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    const { data } = await httpService.get<UploadCustomPostcardImageResponse[]>(
      `/networks/dashboards/${selectedDashboard}/orders/postcard/custom`,
    );

    data.forEach((image) => {
      image.thumbnail = getImagePath(image.thumbnail);
    });
    return data;
  },

  uploadCustomPostcard: async (file: File): Promise<UploadCustomPostcardResponse> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    const formData = new FormData();
    formData.append('file', file);
    return httpService
      .post(`/networks/dashboards/${selectedDashboard}/orders/postcard/custom`, formData)
      .then(({ data }) => {
        return {
          id: data.id,
          fileName: data.fileName,
          size: data.size,
          fileExtension: data.fileExtension,
        };
      })
      .catch((error) => error);
  },

  deleteCustomPostcardImage: async (id: number): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    await httpService.delete(`/networks/dashboards/${selectedDashboard}/orders/postcard/custom/${id}`);
  },

  getDiscount: async (code: string): Promise<number> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService
      .get<DiscountResponse>(`/networks/dashboards/${selectedDashboard}/orders/discount/${code}`)
      .then(({ data }) => data.discount);
  },

  getAllPostcardTemplates: async (): Promise<PostcardTemplate[]> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService
      .get<PostcardTemplate[]>(`/postcards/templates`, { params: { dashboardIds: selectedDashboard } })
      .then(({ data }) => data);
  },

  updatePostcardcardOrder: async (order: UpdatePostCardOrderRequest): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService.patch(`/networks/dashboards/${selectedDashboard}/orders/postcards/${order.orderId}`, order).then(() => {
      const postcard = (store.getState() as RootState).communications.communications[
        getSelectedCommunity()?.id!
      ]?.postcards.find((c) => c.orderId === order.orderId);
      store.dispatch(
        updatePostcard({
          communityId: getSelectedCommunity()?.id!,
          postcard: { ...postcard!, ...order },
        }),
      );
    });
  },
};

export default orderService;

interface UpdatePostCardOrderRequest {
  message: string;
  sendDate: Date;
  senderAddress: Address;
  template: string;
  orderId: number;
}
